/**
 * Layout Component
 * Main application layout with responsive sidebar navigation
 * - Desktop: collapsible fixed sidebar
 * - Mobile/Tablet: overlay drawer with backdrop
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User as UserIcon,
  AlertTriangle,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const baseNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Subjects', href: '/subjects', icon: Users },
    { name: 'Queries', href: '/queries', icon: MessageSquare },
  ];

  const navigation = user?.role === 'admin'
    ? [...baseNavigation, { name: 'Audit Trail', href: '/audit', icon: ClipboardList }]
    : user?.role === 'site-rc'
    ? [...baseNavigation, { name: 'Site Users', href: '/admin/users', icon: Users }]
    : baseNavigation;

  const adminNavigation = [
    { name: 'Sites', href: '/admin/sites', icon: LayoutDashboard },
    { name: 'Users', href: '/admin/users', icon: Users },
  ];

  const cecNavigation = [
    { name: 'CEC Dashboard', href: '/cec', icon: AlertTriangle },
  ];

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  // Shared sidebar content (used for both desktop and mobile)
  const sidebarContent = (showLabels: boolean) => (
    <>
      {/* Navigation */}
      <nav className="mt-6 px-3 overflow-y-auto flex-1">
        <div className="space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(item.href)
                  ? 'bg-opci-gold text-white'
                  : 'text-gray-300 hover:bg-opci-navy-400 hover:text-white'
              }`}
            >
              <item.icon size={20} className="flex-shrink-0" />
              {showLabels && <span>{item.name}</span>}
            </Link>
          ))}
        </div>

        {/* CEC Section */}
        {(user?.role === 'cec' || user?.role === 'admin') && (
          <>
            <div className="mt-8 mb-2">
              {showLabels && (
                <p className="px-3 text-xs font-semibold text-opci-gold-300 uppercase tracking-wider">
                  CEC Adjudication
                </p>
              )}
            </div>
            <div className="space-y-1">
              {cecNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-opci-gold text-white'
                      : 'text-gray-300 hover:bg-opci-navy-400 hover:text-white'
                  }`}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  {showLabels && <span>{item.name}</span>}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Admin Section */}
        {user?.role === 'admin' && (
          <>
            <div className="mt-8 mb-2">
              {showLabels && (
                <p className="px-3 text-xs font-semibold text-opci-gold-300 uppercase tracking-wider">
                  Administration
                </p>
              )}
            </div>
            <div className="space-y-1">
              {adminNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-opci-gold text-white'
                      : 'text-gray-300 hover:bg-opci-navy-400 hover:text-white'
                  }`}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  {showLabels && <span>{item.name}</span>}
                </Link>
              ))}
            </div>
          </>
        )}
      </nav>

      {/* User Section at Bottom */}
      <div className="p-3 border-t border-opci-navy-400">
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-opci-navy-400 hover:text-white rounded-lg transition-colors"
          >
            <div className="w-8 h-8 bg-opci-navy-400 rounded-full flex items-center justify-center flex-shrink-0">
              <UserIcon size={16} />
            </div>
            {showLabels && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.role === 'site-rc' ? 'Site RC' :
                     user?.role === 'site' ? 'Site User' :
                     user?.role === 'admin' ? 'Admin' :
                     user?.role === 'analyst' ? 'Analyst' :
                     user?.role === 'cec' ? 'CEC' : user?.role}
                  </p>
                </div>
                <ChevronDown size={16} className={`transition-transform flex-shrink-0 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>

          {/* User Dropdown Menu */}
          {userMenuOpen && showLabels && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-opci-navy-600 rounded-lg shadow-lg py-1">
              <Link
                to="/profile"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:bg-opci-navy-400"
              >
                <UserIcon size={16} />
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-gray-300 hover:bg-opci-navy-400"
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar (overlay drawer) */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-opci-navy flex flex-col transform transition-transform duration-300 md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 bg-opci-navy-600 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src="/opci-logo-white.png" alt="OPCI" className="w-10 h-10 object-contain" />
            <div>
              <span className="text-white font-bold text-lg block leading-tight">OPCI EDC</span>
              {user?.studyCode && (
                <span className="text-opci-gold-300 text-xs font-medium">{user.studyCode}</span>
              )}
            </div>
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="text-opci-navy-200 hover:text-white p-1"
          >
            <X size={20} />
          </button>
        </div>
        {sidebarContent(true)}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex md:flex-col fixed inset-y-0 left-0 z-50 bg-opci-navy transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 bg-opci-navy-600 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src="/opci-logo-white.png" alt="OPCI" className="w-10 h-10 object-contain" />
            {sidebarOpen && (
              <div>
                <span className="text-white font-bold text-lg block leading-tight">OPCI EDC</span>
                {user?.studyCode && (
                  <span className="text-opci-gold-300 text-xs font-medium">{user.studyCode}</span>
                )}
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-opci-navy-200 hover:text-white p-1"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {sidebarContent(sidebarOpen)}
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
        {/* Top Bar */}
        <header className="bg-white shadow-sm h-16 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-gray-600 hover:text-gray-900 p-1"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              {navigation.find(n => isActive(n.href))?.name ||
               cecNavigation.find(n => isActive(n.href))?.name ||
               adminNavigation.find(n => isActive(n.href))?.name ||
               'OPCI EDC'}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-gray-500">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
